import { Collection, Customer, Product, Variant } from '../shop';
import { Cart, Line } from '../shop/models/cart.model';

const PageViewAsync = async (pageType: string = 'Other', customer?: Customer) => {
  const sh_info_obj = {
    event: 'sh_info',
    page_type: pageType || 'Other',
    page_currency: 'EUR',
    analyzify_source: 'advanced',
    implementation_type: 'headless',
    user: {
      type: 'visitor',
    } as any,
  };
  if (customer) {
    sh_info_obj.user = {
      type: 'member',
      first_name: customer.firstName || null,
      last_name: customer.lastName || null,
      email: customer.email || null,
      zip: customer.defaultAddress?.zip || null,
      phone: customer.defaultAddress?.phone || null,
      country: customer.defaultAddress?.country || null,
      countryCode: customer.defaultAddress?.countryCodeV2 || null,
      city: customer.defaultAddress?.city || null,
      address1: customer.defaultAddress?.address1 || null,
      address2: customer.defaultAddress?.address2 || null,
      province: customer.defaultAddress?.province || null,
      provinceCode: customer.defaultAddress?.provinceCode || null,
    };
  }

  (<any>window).dataLayer.push(sh_info_obj);
};

const CollectionPageView = (products: Partial<Product>[], collection?: Collection) => {
  (<any>window).dataLayer.push({
    event: 'ee_view_item_list',
    ecommerce: {
      item_list_id: collection?.id.split('/').pop() || null,
      item_list_name: collection?.title || null,
      analyzify_source: 'advanced',
      implementation_type: 'headless',
      items: products.map((product, index) => ({
        index: index + 1,
        item_brand: product.vendor || null,
        item_type: product.productType || null,
        item_sku: product.variants?.[0].sku || null,
        item_name: product.title?.replace(/["']/g, '') || null,
        item_handle: product.handle,
        item_id: product.id?.split('/').pop() || null,
        quantity: +(product.variants?.[0]?.quantityAvailable || 1),
        price: +(product.priceRange?.minVariantPrice.amount || 0),
        id: product.id?.split('/').pop() || null,
        item_variant: product.variants?.[0].title || null,
        item_variant_id: product.variants?.[0].id.split('/').pop() || null,
        business_vertical: 'retail',
      })),
    },
  });
};

const ProductPageView = (product: Partial<Product>, variant?: Variant) => {
  (<any>window).dataLayer.push({
    event: 'ee_view_item',
    ecommerce: {
      currency: 'EUR',
      value: +(product.priceRange?.minVariantPrice.amount || 0),
      analyzify_source: 'advanced',
      implementation_type: 'headless',
      items: [
        {
          index: 1,
          item_id: product.id?.split('/').pop() || null,
          item_name: product.title?.replace(/["']/g, '') || null,
          item_brand: product.vendor || null,
          item_type: product.productType || null,
          item_sku: variant?.sku || product.variants?.[0].sku || null,
          item_handle: product.handle || null,
          quantity: +((variant || product.variants?.[0])?.quantityAvailable || 1),
          item_variant: (variant || product.variants?.[0])?.title || null,
          item_variant_id: (variant || product.variants?.[0])?.id.split('/').pop() || null,
          price: +(variant?.price?.amount || product.variants?.[0].price?.amount),
          id: product.id?.split('/').pop() || null,
          google_business_vertical: 'retail',
        },
      ],
    },
  });
};

const AddToCart = (product: Partial<Product>, variant: Variant, quantity: number) => {
  (<any>window).dataLayer.push({
    event: 'ee_add_to_cart',
    ecommerce: {
      currency: 'EUR',
      value: +(variant.price?.amount * quantity),
      analyzify_source: 'advanced',
      implementation_type: 'headless',
      items: [
        {
          index: 1,
          item_id: product.id?.split('/').pop() || null,
          item_name: product.title?.replace(/["']/g, '') || null,
          item_brand: product.vendor || null,
          item_type: product.productType || null,
          item_sku: variant.sku || null,
          item_handle: product.handle || null,
          quantity: +quantity,
          item_variant: variant.title || null,
          item_variant_id: variant.id.split('/').pop() || null,
          price: +variant.price?.amount,
          id: product.id?.split('/').pop() || null,
          google_business_vertical: 'retail',
        },
      ],
    },
  });
};

const AddItemToCart = (cartLine: Line, quantity: number) => {
  (<any>window).dataLayer.push({
    event: 'ee_add_to_cart',
    ecommerce: {
      currency: 'EUR',
      value: +cartLine.cost.amountPerQuantity.amount,
      analyzify_source: 'advanced',
      implementation_type: 'headless',
      items: [
        {
          index: 1,
          item_id: cartLine.merchandise.product.id.split('/').pop() || null,
          item_name: cartLine.merchandise.product.title?.replace(/["']/g, '') || null,
          item_brand: cartLine.merchandise.vendor || null,
          item_type: cartLine.merchandise.product.productType || null,
          item_sku: cartLine.merchandise.sku || null,
          item_handle: cartLine.merchandise.product.handle || null,
          quantity: +(quantity > 0 ? quantity : cartLine.quantity),
          item_variant: cartLine.merchandise.title || null,
          item_variant_id: cartLine.merchandise.id.split('/').pop() || null,
          price: +cartLine.cost.amountPerQuantity.amount,
          id: cartLine.merchandise.product.id.split('/').pop() || null,
          google_business_vertical: 'retail',
        },
      ],
    },
  });
};

const RemoveFromCart = (cartLine: Line, quantity: number = 0) => {
  (<any>window).dataLayer.push({
    event: 'ee_remove_from_cart',
    ecommerce: {
      currency: 'EUR',
      value: +cartLine.cost.amountPerQuantity.amount,
      analyzify_source: 'advanced',
      implementation_type: 'headless',
      items: [
        {
          index: 1,
          item_id: cartLine.merchandise.product.id.split('/').pop() || null,
          item_name: cartLine.merchandise.product.title?.replace(/["']/g, '') || null,
          item_brand: cartLine.merchandise.vendor || null,
          item_type: cartLine.merchandise.product.productType || null,
          item_sku: cartLine.merchandise.sku || null,
          item_handle: cartLine.merchandise.product.handle || null,
          quantity: +(quantity > 0 ? quantity : cartLine.quantity),
          item_variant: cartLine.merchandise.title || null,
          item_variant_id: cartLine.merchandise.id.split('/').pop() || null,
          price: +cartLine.cost.amountPerQuantity.amount,
          id: cartLine.merchandise.product.id.split('/').pop() || null,
          google_business_vertical: 'retail',
        },
      ],
    },
  });
};

const ViewCart = (cart: Cart) => {
  (<any>window).dataLayer.push({
    event: 'ee_view_cart',
    page_type: 'cart',
    ecommerce: {
      currency: 'EUR',
      value: +cart.cost.totalAmount.amount,
      total_item: +cart.lines.length,
      total_quantity: +cart.lines.reduce((acc, line) => acc + line.quantity, 0),
      analyzify_source: 'advanced',
      implementation_type: 'headless',
      items: cart.lines.map((line, index) => ({
        index: index + 1,
        item_id: line.merchandise.product.id.split('/').pop() || null,
        item_name: line.merchandise.product.title?.replace(/["']/g, '') || null,
        item_brand: line.merchandise.vendor || null,
        item_type: line.merchandise.product.productType || null,
        item_sku: line.merchandise.sku || null,
        item_handle: line.merchandise.product.handle || null,
        quantity: +line.quantity,
        item_variant: line.merchandise.title || null,
        item_variant_id: line.merchandise.id.split('/').pop() || null,
        price: +line.cost.amountPerQuantity.amount,
        id: line.merchandise.product.id.split('/').pop() || null,
        google_business_vertical: 'retail',
      })),
    },
  });
};

const CheckoutStart = (cart: Cart) => {
  (<any>window).dataLayer.push({
    event: 'ee_begin_checkout',
    page_type: 'cart',
    ecommerce: {
      currency: 'EUR',
      value: +cart.cost.totalAmount.amount,
      total_item: cart.lines.length,
      total_quantity: cart.lines.reduce((acc, line) => acc + line.quantity, 0),
      analyzify_source: 'advanced',
      implementation_type: 'headless',
      items: cart.lines.map((line, index) => ({
        index: index + 1,
        item_id: line.merchandise.product.id.split('/').pop() || null,
        item_name: line.merchandise.product.title?.replace(/["']/g, '') || null,
        item_brand: line.merchandise.vendor || null,
        item_type: line.merchandise.product.productType || null,
        item_sku: line.merchandise.sku || null,
        item_handle: line.merchandise.product.handle || null,
        quantity: +line.quantity,
        item_variant: line.merchandise.title || null,
        item_variant_id: line.merchandise.id.split('/').pop() || null,
        price: +line.cost.amountPerQuantity.amount,
        id: line.merchandise.product.id.split('/').pop() || null,
        google_business_vertical: 'retail',
      })),
    },
  });
};

const LoginStart = () => {
  (<any>window).dataLayer.push({
    event: 'ee_login',
  });
};

const SignupStart = () => {
  (<any>window).dataLayer.push({
    event: 'ee_signup',
  });
};

const Search = (query: string, products: Partial<Product>[]) => {
  (<any>window).dataLayer.push({
    event: 'ee_search',
    page_type: 'search',
    search_term: query,
    search_results: products.length,
    ecommerce: {
      currency: 'EUR',
      value: products.reduce((total, product) => total + (product.priceRange?.minVariantPrice.amount || 0), 0),
      total_item: products.length,
      total_quantity: products.reduce((total, product) => total + (product.variants?.[0]?.quantityAvailable || 1), 0),
      analyzify_source: 'advanced',
      implementation_type: 'headless',
      items: products.map((product, index) => ({
        index: index + 1,
        item_brand: product.vendor,
        item_type: product.productType,
        item_sku: product.variants?.[0].sku,
        item_name: product.title?.replace(/["']/g, ''),
        item_handle: product.handle,
        item_id: product.id?.split('/').pop(),
        quantity: product.variants?.[0].quantityAvailable || 1,
        item_variant: product.variants?.[0].title,
        item_variant_id: product.variants?.[0].id.split('/').pop(),
        price: product.priceRange?.minVariantPrice.amount,
        id: product.id?.split('/').pop(),
        business_vertical: 'retail',
      })),
    },
  });
};

export const Analyzify = {
  PageViewAsync,
  CollectionPageView,
  ProductPageView,
  AddToCart,
  AddItemToCart,
  RemoveFromCart,
  ViewCart,
  CheckoutStart,
  LoginStart,
  SignupStart,
  Search,
};

async function hashSha256(string: string) {
  const utf8 = new TextEncoder().encode(string);
  const hashBuffer = await crypto.subtle.digest('SHA-256', utf8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(bytes => bytes.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

async function hashSha1(string: string) {
  const utf8 = new TextEncoder().encode(string);
  const hashBuffer = await crypto.subtle.digest('SHA-1', utf8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(bytes => bytes.toString(16).padStart(2, '0')).join('');
  return hashHex;
}
